<template>
  <div
    class="d-flex flex-column"
    style="gap: 24px; border-radius: 8px; width: 100%"
  >
    <div class="container-plot">
      <div>
        <v-row no-gutters align="center" justify="space-between">
          <span class="body-bold-medium neutral-20-dark--text">
            {{ $t('message.pages.analyticsV3.screen_bandwidth.title') }}
          </span>

          <span class="body-regular-smaller neutral-40-light--text">
            {{
              $t('message.pages.analyticsV3.pages.devices.updatedAgo', {
                time: lastUpdatedMinutes,
              })
            }}
          </span>
        </v-row>

        <span class="body-regular-smaller neutral-40-light--text">
          {{ $t('message.pages.analyticsV3.screen_bandwidth.description') }}
          <a
            type="secondary"
            target="_blank"
            @click="handleGoToAnalyticsArticle"
          >
            <span class="body-bold-smaller knowMore mr-1">{{
              $t('message.actions.knowMore')
            }}</span>
            <v-icon size="12" color="neutral-30-base">mdi-open-in-new</v-icon>
          </a>
        </span>
      </div>

      <v-row no-gutters align="center" justify="space-between">
        <div class="d-flex flex-wrap align-center">
          <div
            class="metric-btn d-flex align-center"
            v-for="(item, index) in chartSeries"
            :key="index"
          >
            <div
              class="d-flex align-center pa-1"
              :class="{
                'metric-btn--opacity':
                  !!filterByMetric && filterByMetric !== item.name,
              }"
              style="gap: 8px"
              @click="() => handleFilterByMetric(item.name)"
            >
              <v-badge
                class="my-badge-scope"
                inline
                :color="metricColors[item.name]"
              />

              <div class="d-flex align-center" style="user-select: none">
                <span class="body-regular-smaller neutral-20-dark--text mr-1">
                  {{
                    $t(
                      `message.pages.analyticsV3.screen_bandwidth.${item.name}`
                    )
                  }}<span v-if="item.name !== 'cost'">:</span>
                </span>

                <v-skeleton-loader
                  v-if="loading && item.name !== 'cost'"
                  width="36"
                  height="14"
                  type="image"
                />

                <span
                  v-else-if="item.name !== 'cost'"
                  class="body-regular-smaller neutral-20-dark--text"
                >
                  {{ calcMegabytes(sumPrecise(item.data), 'MB', 'GB', true) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex align-center">
          <base-select
            v-model="showPlotBy"
            :items="showPlotByPlaceholders"
            :menu-props="{ top: false, offsetY: true }"
            item-text="name"
            item-value="value"
            dense
            hide-details
            attach
            :style="`width: ${$i18next.language === 'es' ? 169 : 124}px`"
          />
        </div>
      </v-row>

      <v-skeleton-loader v-if="loading" type="image" height="100%" />

      <div class="dual-chart" v-else>
        <div class="chart-main">
          <apexchart
            ref="barChart"
            height="300"
            width="100%"
            :options="getChartOptions"
            :series="chartSeries"
          />
          <apexchart
            v-if="chartSeries.length > 0"
            class="pdf-export-chart"
            ref="fakeChart"
            type="line"
            height="380"
            width="878"
            :options="pdfOptions"
            :series="chartSeries"
            :key="'fake'"
          />
        </div>

        <div class="axis-labels-container">
          <span class="cost-label">
            {{ $t('message.pages.analyticsV3.screen_bandwidth.cost') }} (R$)
          </span>
        </div>
      </div>

      <v-col class="pa-0">
        <v-row no-gutters class="d-flex justify-space-between align-center">
          <v-row no-gutters>
            <v-switch
              v-model="showCurrentCycle"
              class="my-0 py-0"
              hide-details
            />
            <span class="body-regular-small neutral-30-base--text">{{
              $t('message.pages.analyticsV3.screen_bandwidth.currentCycle')
            }}</span>
          </v-row>

          <base-btn
            type="tertiary"
            size="small"
            @click="handleOpenBandwidthDetailsModal"
            >{{
              $t('message.pages.analyticsV3.screen_bandwidth.bandwidthDetails')
            }}
            <v-icon size="20" class="ml-1">mdi-arrow-expand</v-icon></base-btn
          >
        </v-row>

        <v-row no-gutters class="content-cards-row" v-if="showCurrentCycle">
          <v-col
            class="pa-0"
            v-for="(item, index) in cycleViewsValues"
            :key="index"
          >
            <v-card flat class="cards-container">
              <div class="content-card-infos">
                <v-tooltip max-width="200px" top>
                  <template v-slot:activator="{ on, attrs }">
                    <span class="body-label-caption neutral-40-light--text">
                      {{ item.name }}
                    </span>

                    <v-icon
                      v-if="item.tooltip"
                      size="11"
                      color="neutral-70"
                      v-on="on"
                      v-bind="attrs"
                      class="mt-0 mb-1"
                    >
                      mdi-information
                    </v-icon>
                  </template>

                  <v-row no-gutters>
                    <span>{{ item.tooltip }}</span>
                  </v-row>
                </v-tooltip>
              </div>

              <v-skeleton-loader
                v-if="loading"
                width="84"
                height="29"
                type="image"
              />

              <span v-else class="body-bold-large neutral-20-dark--text">{{
                item.value
              }}</span>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </div>

    <div v-if="isCapturingPng" class="capture-clone">
      <div ref="chartContainerForCapture" style="width: 100%; height: 100%; background: white; padding: 24px;">
        <div>
            <v-row no-gutters>
                <span class="body-bold-medium" style="color: #282931">
                    {{ $t('message.pages.analyticsV3.screen_bandwidth.title') }}
                </span>
            </v-row>
            <span class="body-regular-smaller" style="color: #636578">
                {{ $t('message.pages.analyticsV3.screen_bandwidth.description') }}
            </span>
        </div>
        <v-row no-gutters class="mt-6" align="center">
            <div class="d-flex flex-wrap align-center">
                <div class="metric-btn d-flex align-center" v-for="(item, index) in chartSeries" :key="index">
                    <div class="d-flex align-center pa-1" style="gap: 8px">
                        <v-badge class="my-badge-scope" inline :color="metricColors[item.name]" />
                        <div class="d-flex align-center" style="user-select: none">
                            <span class="body-regular-smaller mr-1" style="color: #282931">
                                {{ $t(`message.pages.analyticsV3.screen_bandwidth.${item.name}`) }}<span v-if="item.name !== 'cost'">:</span>
                            </span>
                            <span v-if="item.name !== 'cost'" class="body-regular-smaller" style="white-space: nowrap; color: #282931;">
                                {{ calcMegabytes(sumPrecise(item.data), 'MB', 'GB', true) }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </v-row>
        <div class="dual-chart">
            <div class="chart-main">
                <apexchart
                    v-if="chartSeries.length > 0"
                    height="300"
                    width="100%"
                    :options="pdfOptions"
                    :series="chartSeries"
                />
            </div>
            <div class="axis-labels-container">
                <span class="cost-label">
                    {{ $t('message.pages.analyticsV3.screen_bandwidth.cost') }} (R$)
                </span>
            </div>
        </div>
      </div>
    </div>

    <div class="container-table">
      <v-input
        class="search-bar"
        v-model="searchByUrl"
        size="small"
        hide-details
        :placeholder="
          $t('message.pages.analyticsV3.screen_bandwidth.searchvideo')
        "
        @input="doItemsSearch"
        ><template v-slot:prepend-inner>
          <v-icon class="neutral-60--text" size="16"> mdi-magnify </v-icon>
        </template></v-input
      >

      <BaseTable
        :items="
          searchByUrl.length > 0 ? filteredRankingItems : aggregatedVideosToList
        "
        :columns="baseTableColumns"
        :items-per-page="10"
        :show-pagination="true"
        :is-searching="searchByUrl.length > 0"
        :showActionColumn="true"
        :show-total-label="false"
        :showTotalLabel="false"
        :loading="loading"
        @clear-search="clearSearch"
        @update-visible-columns="currentlyVisibleColumns = $event"
        noPointer
      >
        <template #titleCell="{ row }">
          <span class="truncate-cell" :title="row.title">
            {{ row.title ? row.title : '-' }}
          </span>
        </template>
        <template #playCell="{ row }">
          <span class="body-regular-small truncate-cell">
            {{ autoMegabytes(row.play) }}
          </span>
        </template>
        <template #downloadCell="{ row }">
          <span class="body-regular-small truncate-cell">
            {{ autoMegabytes(row.download) }}
          </span>
        </template>
        <template #drmCell="{ row }">
          <span class="body-regular-small truncate-cell">
            {{ autoMegabytes(row.drm) }}
          </span>
        </template>
        <template #totalConsumptionCell="{ row }">
          <span class="body-regular-small truncate-cell">
            {{ autoMegabytes(row.totalConsumption, 'MB') }}
          </span>
        </template>
        <template #costCell="{ row }">
          <span class="body-regular-small truncate-cell">
            {{
              gbCost
                ? (Number(row.cost) / 100).toLocaleString(currencyLocale, {
                    style: 'currency',
                    currency: currency,
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : 'N/A'
            }}
          </span>
        </template>
      </BaseTable>
    </div>
    <BandwidthDetailsModal />
  </div>
</template>

<script>
import { differenceInDays, format, subDays } from 'date-fns'
import { enUS, ptBR, es as esES } from 'date-fns/locale'
import { mapState, mapMutations } from 'vuex'
import Fuse from 'fuse.js'
import VueApexCharts from 'vue-apexcharts'
import BaseSelect from '@/components/BaseComponents/BaseSelect.vue'
import BaseTable from '@/components/BaseComponents/BaseTable.vue'
import BaseBtn from '@/components/BaseBtn.vue'
import calcMegabytesMixin from '@/utils/calcMegabytes.js'
import newAnalyticsApi from '@/services/newAnalyticsApi'
import BandwidthDetailsModal from '../../../AnalyticsV2/components/BandwidthDetailsModal.vue'
import { exportToCsv } from '@/utils/csvExporter.js'
import { exportToPng } from '@/utils/pngExporter.js'
import { generatePdfBlob } from '@/utils/pdfExporter.js'
import { dataFormatter } from '@/utils/dataFormatter'
import currencyMixin from '../../mixins/currencyMixin'

export default {
  name: 'Bandwidth',
  components: {
    apexchart: VueApexCharts,
    BaseSelect,
    BaseTable,
    BaseBtn,
    BandwidthDetailsModal,
  },
  mixins: [calcMegabytesMixin, currencyMixin],
  data() {
    return {
      isCapturingPng: false,
      loading: false,
      errorOnFetch: false,
      showCurrentCycle: false,
      aggregatedDataToPlot: [],
      aggregatedVideosToList: [],
      filteredRankingItems: [],
      lastUpdatedAt: 0,
      lastUpdatedTimeout: null,
      chartSeries: [
        { name: 'play' },
        { name: 'download' },
        { name: 'drm' },
        { name: 'cost' },
      ],
      chartCategories: [],
      showPlotBy: 'daily',
      filterByMetric: null,
      searchByUrl: '',
      metricColors: {
        play: '#3F55EE',
        download: '#20C1A4',
        drm: '#FF882B',
        cost: '#FFC107',
      },
      analyticsLinks: {
        pt: 'https://help.pandavideo.com/pt-br/article/como-funcionam-as-metricas-do-analytics-na-panda-video-qk2m59/',
        en: 'https://help.pandavideo.com/en-us/article/how-do-analytics-metrics-work-in-panda-video-kinu6m/',
        es: 'https://help.pandavideo.com/es/article/como-funcionan-las-metricas-de-analytics-en-panda-video-cpkcfv/',
      },
      currentlyVisibleColumns: [],
    }
  },
  computed: {
    ...mapState({
      dateRange: (state) => state.analytics.filters.dateRange,
      userPlanInfo: (state) => state.user.userPlanInfo,
      gbCost: (state) => state.user.userPlanInfo?.limit?.gb_cost,
      selectedVideos: (state) =>
        state.analytics.filters.filterVideos.selectedVideos,
    }),
    isRangeMoreThan30Days() {
      if (!this.dateRange || !this.dateRange.start || !this.dateRange.end)
        return false
      const start = this.dateRange.start
      const end = this.dateRange.end
      const diff = differenceInDays(end, start)
      return diff >= 30
    },
    rotateAlways() {
      return this.isRangeMoreThan30Days && this.showPlotBy === 'daily'
    },
    cycleViewsValues() {
      return [
        {
          name: this.$t(
            'message.pages.analyticsV3.screen_bandwidth.contracted'
          ),
          tooltip: this.$t(
            'message.pages.analyticsV3.screen_bandwidth.contractedTooltip'
          ),
          value: this.calcMegabytes(
            this.userPlanInfo.limit?.storage_plan,
            'MB',
            'GB',
            true
          ),
        },
        {
          name: this.$t('message.pages.analyticsV3.screen_bandwidth.used'),
          tooltip: this.$t(
            'message.pages.analyticsV3.screen_bandwidth.usedTooltip'
          ),
          value: this.calcMegabytes(
            this.userPlanInfo.limit?.traffic,
            'MB',
            'GB',
            true
          ),
        },
        {
          name: this.$t(
            'message.pages.analyticsV3.screen_bandwidth.estimatedEndCycle'
          ),
          tooltip: this.$t(
            'message.pages.analyticsV3.screen_bandwidth.estimatedEndCycleTooltip'
          ),
          value: this.calcMegabytes(
            this.userPlanInfo.limit?.estimated_traffic,
            'MB',
            'GB',
            true
          ),
        },
        {
          name: this.$t('message.pages.analyticsV3.screen_bandwidth.endCycle'),
          tooltip: this.$t(
            'message.pages.analyticsV3.screen_bandwidth.endCycleTooltip'
          ),
          value: (() => {
            const end = this.userPlanInfo.limit?.end_period
            if (!end) return '-'
            const endDate = new Date(end)
            const now = new Date()
            endDate.setHours(0, 0, 0, 0)
            now.setHours(0, 0, 0, 0)
            const diff = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24))

            return this.$t(
              'message.pages.analyticsV3.screen_bandwidth.endInDays',
              {
                days: diff,
                plural: diff !== 1 ? 's' : '',
              }
            )
          })(),
        },
      ]
    },
    baseTableColumns() {
      return [
        {
          text: this.$t('message.pages.analyticsV3.screen_bandwidth.header'),
          value: 'title',
          align: 'start',
          width: '320px',
        },
        {
          text: this.$t('message.pages.analyticsV3.screen_bandwidth.play'),
          value: 'play',
          align: 'end',
          tooltip: this.$t(
            'message.pages.analyticsV3.screen_bandwidth.playTooltip'
          ),
          formatter: (value, row) => {
            return this.calcMegabytes(row.play, 'B', 'GB', true)
          },
        },
        {
          text: this.$t('message.pages.analyticsV3.screen_bandwidth.download'),
          value: 'download',
          align: 'end',
          tooltip: this.$t(
            'message.pages.analyticsV3.screen_bandwidth.downloadTooltip'
          ),
          formatter: (value, row) => {
            return this.calcMegabytes(row.download, 'B', 'GB', true)
          },
        },
        {
          text: this.$t('message.pages.analyticsV3.screen_bandwidth.drm'),
          value: 'drm',
          align: 'end',
          tooltip: this.$t(
            'message.pages.analyticsV3.screen_bandwidth.drmTooltip'
          ),
          formatter: (value, row) => {
            return this.calcMegabytes(row.drm, 'B', 'GB', true)
          },
        },
        {
          text: this.$t(
            'message.pages.analyticsV3.screen_bandwidth.totalConsumption'
          ),
          value: 'totalConsumption',
          align: 'end',
          tooltip: this.$t(
            'message.pages.analyticsV3.screen_bandwidth.totalConsumptionTooltip'
          ),
          formatter: (value, row) => {
            return this.calcMegabytes(row.totalConsumption, 'MB', 'GB', true)
          },
        },
        {
          text: this.$t('message.pages.analyticsV3.screen_bandwidth.cost'),
          value: 'cost',
          align: 'end',
          tooltip: this.$t(
            'message.pages.analyticsV3.screen_bandwidth.costTooltip'
          ),
          formatter: (value, row) => {
            const cost = Number(row.cost)
            if (!cost || isNaN(cost)) {
              return '="R$ 0,00"'
            }
            const formattedCost = `R$ ${(Number(cost) / 100).toLocaleString(
              'pt-BR',
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }
            )}`
            return `="${formattedCost}"`
          },
        },
      ]
    },
    costMax() {
      const costSerie = this.chartSeries.find((item) => item.name === 'cost')
      if (!costSerie || !Array.isArray(costSerie.data)) {
        return 0
      }
      return Math.max(...costSerie.data)
    },
    maxYaxis() {
      if (!Array.isArray(this.chartSeries)) return 0

      const nonCostSeries = this.chartSeries.filter(
        (item) => item.name !== 'cost' && Array.isArray(item.data)
      )
      if (nonCostSeries.length === 0) return 0

      const totalMaxSum = nonCostSeries.reduce((acc, serie) => {
        const values = serie.data.map((v) => Number(v) || 0)
        const maxInSerie = values.length ? Math.max(...values) : 0
        return acc + maxInSerie
      }, 0)

      return totalMaxSum
    },
    tickAmount() {
      return 4
    },
    niceCostMax() {
      const m = this.costMax
      const t = this.tickAmount
      return Math.ceil(m / t) * t
    },
    costTicks() {
      const count = this.tickAmount + 1
      const step = this.niceCostMax / (count - 1)

      return Array.from({ length: count }, (_, i) =>
        Math.round(i * step)
      ).reverse()
    },
    locale() {
      return this.$i18next.resolvedLanguage
    },
    showPlotByPlaceholders() {
      return [
        {
          value: 'daily',
          name: this.$i18next.t('message.pages.analyticsV3.daily'),
        },
        {
          value: 'weekly',
          name: this.$i18next.t('message.pages.analyticsV3.weekly'),
        },
        {
          value: 'monthly',
          name: this.$i18next.t('message.pages.analyticsV3.monthly'),
        },
      ]
    },
    getChartOptions() {
      const textColor = this.$vuetify.theme.currentTheme['neutral-40-light']
      const gridColor = this.$vuetify.theme.currentTheme['neutral-95-lightest']
      const isDark = this.$vuetify.theme.dark
      const localeMap = { pt: ptBR, es: esES, en: enUS }
      const localeDateFns = localeMap[this.locale] || enUS

      return {
        chart: {
          type: 'line',
          height: 300,
          width: '100%',
          stacked: true,
          stackOnlyBar: true,
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: false },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '50%',
            distributed: false,
          },
        },
        dataLabels: { enabled: false },
        stroke: {
          width: 2,
        },
        grid: {
          borderColor: gridColor,
          strokeDashArray: 0,
          yaxis: {
            lines: {
              show: true,
              color: gridColor,
            },
          },
          xaxis: {
            lines: {
              show: false,
            },
          },
        },
        xaxis: {
          tickAmount: 30,
          categories: this.chartCategories,
          tooltip: {
            enabled: false,
          },
          axisBorder: {
            show: true,
            color: gridColor,
          },
          axisTicks: {
            show: true,
            color: gridColor,
          },
          labels: {
            style: {
              fontFamily: 'Rubik, sans-serif',
              fontSize: '11px',
              fontWeight: 400,
              colors: textColor,
            },
            formatter: (value) => {
              if (!value) return value
              const showBy = this.showPlotBy
              if (showBy === 'daily') {
                return dataFormatter(value, localeDateFns)
              }
              if (showBy === 'weekly') {
                const [year, week] = value.split('-W')
                const firstDay = this.getDateOfISOWeek(+week, +year)
                const lastDay = new Date(firstDay)
                lastDay.setDate(firstDay.getDate() + 6)
                const sameMonth = firstDay.getMonth() === lastDay.getMonth()
                const startStr = format(firstDay, sameMonth ? 'dd' : 'dd/MM', {
                  locale: localeDateFns,
                })
                const endStr = format(lastDay, 'dd/MM', {
                  locale: localeDateFns,
                })
                return `${startStr} - ${endStr}`
              }
              if (showBy === 'monthly') {
                const [year, month] = value.split('-').map(Number)
                const date = new Date(year, month - 1, 1)
                const monthStr = format(date, 'MMMM', { locale: localeDateFns })

                return monthStr.charAt(0).toUpperCase() + monthStr.slice(1)
              }
              return value
            },
            rotateAlways: this.rotateAlways,
          },
        },
        yaxis: [
          {
            seriesName: 'play',
            tickAmount: this.tickAmount,
            min: 0,
            max: this.maxYaxis,
            labels: {
              style: {
                fontFamily: 'Rubik, sans-serif',
                fontSize: '11px',
                fontWeight: 400,
                colors: textColor,
              },
              formatter: (value) => {
                const valueCalc = this.calcMegabytes(value, 'MB')
                return valueCalc
              },
            },
          },
          {
            seriesName: 'download',
            tickAmount: this.tickAmount,
            min: 0,
            max: this.maxYaxis,
            labels: { show: false },
          },
          {
            seriesName: 'drm',
            tickAmount: this.tickAmount,
            min: 0,
            max: this.maxYaxis,
            labels: { show: false },
          },
          {
            tickAmount: this.tickAmount,
            opposite: true,
            min: 0,
            labels: {
              style: {
                fontFamily: 'Rubik, sans-serif',
                fontSize: '11px',
                fontWeight: 400,
                colors: textColor,
              },
              formatter: (value) => {
                return this.roundToNextBase10(
                  Number(value / 100)
                ).toLocaleString('pt-BR', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })
              },
            },
          },
        ],
        legend: { show: false },
        tooltip: {
          theme: isDark ? 'dark' : 'light',
          y: {
            title: {
              formatter: (value) => {
                return this.$t(
                  `message.pages.analyticsV3.screen_bandwidth.${value}`
                )
              },
            },
            formatter: (value, opts) => {
              if (opts.seriesIndex === 3) {
                return this.formatCurrency(value)
              }
              return this.calcMegabytes(value, 'MB', 'MB', true)
            },
          },
        },
      }
    },
    pdfOptions() {
      const textColor = '#636578'
      const gridColor = '#E0E0E0'
      const isDark = this.$vuetify.theme.dark
      const lang = this.locale || 'en'
      const localeMap = { pt: ptBR, es: esES, en: enUS }
      const localeDateFns = localeMap[lang] || enUS

      return {
        chart: {
          type: 'line',
          stacked: true,
          stackOnlyBar: true,
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: false },
        },
        stroke: {
          width: 2,
        },
        grid: {
          borderColor: gridColor,
          strokeDashArray: 0,
          yaxis: {
            lines: {
              show: true,
              color: gridColor,
            },
          },
          xaxis: {
            lines: {
              show: false,
            },
          },
        },
        xaxis: {
          tickAmount: 30,
          categories: this.chartCategories,
          tooltip: {
            enabled: false,
          },
          axisBorder: {
            show: true,
            color: gridColor,
          },
          axisTicks: {
            show: true,
            color: gridColor,
          },
          labels: {
            style: {
              fontFamily: 'Rubik, sans-serif',
              fontSize: '11px',
              fontWeight: 400,
              colors: textColor,
            },
            formatter: (value) => {
              if (!value) return value
              const showBy = this.showPlotBy
              if (showBy === 'daily') {
                return dataFormatter(value, localeDateFns)
              }
              if (showBy === 'weekly') {
                const [year, week] = value.split('-W')
                const firstDay = this.getDateOfISOWeek(+week, +year)
                const lastDay = new Date(firstDay)
                lastDay.setDate(firstDay.getDate() + 6)
                const sameMonth = firstDay.getMonth() === lastDay.getMonth()
                const startStr = format(firstDay, sameMonth ? 'dd' : 'dd/MM', {
                  locale: localeDateFns,
                })
                const endStr = format(lastDay, 'dd/MM', {
                  locale: localeDateFns,
                })
                return `${startStr} - ${endStr}`
              }
              if (showBy === 'monthly') {
                const [year, month] = value.split('-').map(Number)
                const date = new Date(year, month - 1, 1)
                const monthStr = format(date, 'MMMM', { locale: localeDateFns })

                return monthStr.charAt(0).toUpperCase() + monthStr.slice(1)
              }
              return value
            },
            rotateAlways: this.rotateAlways,
          },
        },
        yaxis: [
          {
            seriesName: 'play',
            tickAmount: this.tickAmount,
            min: 0,
            max: this.maxYaxis,
            labels: {
              style: {
                fontFamily: 'Rubik, sans-serif',
                fontSize: '11px',
                fontWeight: 400,
                colors: textColor,
              },
              formatter: (value) => {
                const valueCalc = this.calcMegabytes(value, 'MB')
                return valueCalc
              },
            },
          },
          {
            seriesName: 'download',
            tickAmount: this.tickAmount,
            min: 0,
            max: this.maxYaxis,
            labels: { show: false },
          },
          {
            seriesName: 'drm',
            tickAmount: this.tickAmount,
            min: 0,
            max: this.maxYaxis,
            labels: { show: false },
          },
          {
            tickAmount: this.tickAmount,
            opposite: true,
            min: 0,
            labels: {
              style: {
                fontFamily: 'Rubik, sans-serif',
                fontSize: '11px',
                fontWeight: 400,
                colors: textColor,
              },
              formatter: (value) => {
                return this.roundToNextBase10(
                  Number(value / 100)
                ).toLocaleString('pt-BR', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })
              },
            },
          },
        ],
        legend: { show: false },
        tooltip: {
          theme: isDark ? 'dark' : 'light',
          y: {
            title: {
              formatter: (value) => {
                return this.$t(
                  `message.pages.analyticsV3.screen_bandwidth.${value}`
                )
              },
            },
            formatter: (value, opts) => {
              if (opts.seriesIndex === 3) {
                return this.formatCurrency(value)
              }
              return this.calcMegabytes(value, 'MB', 'MB', true)
            },
          },
        },
      }
    },
    lastUpdatedMinutes() {
      return Math.floor(this.lastUpdatedAt / 60) || 1
    },
  },
  methods: {
    ...mapMutations('analytics', [
      'UPDATE_FILTER_DATA_RANGE',
      'UPDATE_DISABLED_DATE_RANGE',
      'UPDATE_ERROR_STATE',
    ]),
    autoMegabytes(value, fromUnit = 'B', toFixed = true) {
      let bytes
      switch (fromUnit) {
        case 'GB':
          bytes = Number(value) * 1024 * 1024 * 1024
          break
        case 'MB':
          bytes = Number(value) * 1024 * 1024
          break
        case 'KB':
          bytes = Number(value) * 1024
          break
        case 'B':
        default:
          bytes = Number(value)
      }

      let toUnit
      if (bytes < 1024 * 1024) {
        toUnit = 'B'
      } else if (bytes < 1024 * 1024 * 1024) {
        toUnit = 'MB'
      } else {
        toUnit = 'GB'
      }

      return this.calcMegabytes(value, fromUnit, toUnit, toFixed)
    },
    async mergeTraffic(trafficData) {
      const gbCostCents = Math.round(Number(this.gbCost))

      const merged = await Promise.all(
        trafficData.map(async (item) => {
          const bytesGeneral = Number(item.bytes_used_general) || 0
          const bytesDownload = Number(item.bytes_used_download) || 0
          const bytesDrm = Number(item.bytes_used_drm) || 0

          const mbGeneral = bytesGeneral / 1024 ** 2
          const mbDownload = bytesDownload / 1024 ** 2
          const mbDrm = bytesDrm / 1024 ** 2

          const totalConsumption = mbGeneral + mbDownload + mbDrm
          const cost = Math.round((totalConsumption / 1024) * gbCostCents)

          const result = {
            video_external_id: item.video_external_id,
            play: bytesGeneral,
            download: bytesDownload,
            drm: bytesDrm,
            totalConsumption,
            cost,
          }

          const [videoInfo] = await window.db.videos
            .where({ video_external_id: item.video_external_id })
            .toArray()

          if (videoInfo) {
            Object.assign(result, videoInfo)
          }

          return result
        })
      )

      return merged
    },
    aggregateTrafficResponses(responses) {
      const acc = new Map()

      for (const resp of responses) {
        const generalArr = resp.data
        const downloadArr = resp.downloadData?.data || []
        const drmArr = resp.drmData?.data || []

        generalArr.forEach(({ t, b = 0 }) => {
          if (!acc.has(t)) acc.set(t, { t, b: 0, b_download: 0, b_drm: 0 })
          acc.get(t).b += b
        })
        downloadArr.forEach(({ t, b = 0 }) => {
          if (!acc.has(t)) acc.set(t, { t, b: 0, b_download: 0, b_drm: 0 })
          acc.get(t).b_download += b
        })
        drmArr.forEach(({ t, b = 0 }) => {
          if (!acc.has(t)) acc.set(t, { t, b: 0, b_download: 0, b_drm: 0 })
          acc.get(t).b_drm += b
        })
      }

      const merged = Array.from(acc.values()).sort((a, b) =>
        a.t.localeCompare(b.t)
      )

      return {
        generalData: { data: merged.map(({ t, b }) => ({ t, b })) },
        downloadData: {
          data: merged.map(({ t, b_download }) => ({ t, b: b_download })),
        },
        drmData: { data: merged.map(({ t, b_drm }) => ({ t, b: b_drm })) },
      }
    },
    mergeDailyBandwidth(generalData, downloadData, drmData) {
      const allDates = new Set([
        ...generalData.data.map((r) => r.t),
        ...downloadData.data.map((r) => r.t),
        ...drmData.data.map((r) => r.t),
      ])

      const gbCost = Math.round(Number(this.gbCost))

      return Array.from(allDates)
        .sort()
        .map((date) => {
          const g = generalData.data.find((r) => r.t === date)
          const d = downloadData.data.find((r) => r.t === date)
          const m = drmData.data.find((r) => r.t === date)

          const toMB = (bytes) => (Number(bytes) || 0) / 1024 ** 2

          const mbGeneral = toMB(g?.b)
          const mbDownload = toMB(d?.b)
          const mbDrm = toMB(m?.b)

          const totalBandwidthMB = mbGeneral + mbDownload + mbDrm

          return {
            title: date,
            b_general: mbGeneral,
            b_download: mbDownload,
            b_drm: mbDrm,
            useds_bandwidth_total: totalBandwidthMB,
            cost: Math.round((totalBandwidthMB / 1024) * gbCost),
          }
        })
    },
    async getSelectedInfo() {
      this.clearTimeIntervals()
      this.loading = true
      try {
        let referencesResponses = []
        for (let video of this.selectedVideos) {
          let ret = await newAnalyticsApi.getVideoTraffic(video.videoId, [
            format(this.dateRange.start, 'yyyy-MM-dd'),
            format(this.dateRange.end, 'yyyy-MM-dd'),
          ])
          referencesResponses.push(ret.data)
        }
        const { generalData, downloadData, drmData } =
          this.aggregateTrafficResponses(referencesResponses)

        const finalChartData = this.mergeDailyBandwidth(
          generalData,
          downloadData,
          drmData
        )

        const [generalVideosResponse] = await Promise.all([
          newAnalyticsApi.getTrafficInRankByType(
            format(this.dateRange.start, 'yyyy-MM-dd'),
            format(this.dateRange.end, 'yyyy-MM-dd'),
            'all'
          ),
        ])

        const unifiedTraffic = await this.mergeTraffic(
          generalVideosResponse.data.data
        )

        const filteredVideos = unifiedTraffic.filter((video) => {
          return this.selectedVideos.some(
            (sel) => sel.videoExternalId === video.video_external_id
          )
        })

        this.aggregatedDataToPlot = this.aggregateDataByDate(finalChartData)
        this.aggregatedVideosToList = filteredVideos

        this.lastUpdatedTimeout = setInterval(() => {
          this.lastUpdatedAt += 1
        }, 1000)
      } catch (error) {
        this.UPDATE_ERROR_STATE(true)
      } finally {
        this.loading = false
      }
    },
    async fetchGlobalMetrics() {
      this.loading = true
      this.clearTimeIntervals()

      try {
        const [generalDailyResponse] = await Promise.all([
          newAnalyticsApi.getTrafficBandwidthByType(
            format(this.dateRange.start, 'yyyy-MM-dd'),
            format(this.dateRange.end, 'yyyy-MM-dd'),
            'all'
          ),
        ])

        const [generalVideosResponse] = await Promise.all([
          newAnalyticsApi.getTrafficInRankByType(
            format(this.dateRange.start, 'yyyy-MM-dd'),
            format(this.dateRange.end, 'yyyy-MM-dd'),
            'all'
          ),
        ])

        const unifiedTraffic = await this.mergeTraffic(
          generalVideosResponse.data.data
        )

        this.aggregatedVideosToList = unifiedTraffic
        this.aggregatedDataToPlot = this.aggregateDataByDate(
          this.parseForAggregate(generalDailyResponse.data.data)
        )

        this.lastUpdatedTimeout = setInterval(() => {
          this.lastUpdatedAt += 1
        }, 1000)
      } catch (e) {
        console.error('Erro ao buscar dados de referência:', e)
      } finally {
        this.loading = false
      }
    },
    parseForAggregate(flatArray) {
      return flatArray.map((item) => {
        const toMB = (bytes) => (Number(bytes) || 0) / 1024 ** 2

        const playBytes = toMB(item.b)
        const downloadBytes = toMB(item.b_download)
        const drmBytes = toMB(item.b_drm)
        const totalBandwidth = playBytes + downloadBytes + drmBytes

        const gbCost = Math.round(Number(this.gbCost))

        return {
          title: item.t,
          b_general: playBytes,
          b_download: downloadBytes,
          b_drm: drmBytes,
          useds_bandwidth_total: totalBandwidth,
          cost: Math.round((totalBandwidth / 1024) * gbCost),
        }
      })
    },
    aggregateDataByDate(data) {
      const dailyMap = {}
      const weeklyMap = {}
      const monthlyMap = {}

      data.forEach((item) => {
        const dateStr = item.title
        const date = new Date(dateStr)

        const dailyKey = dateStr
        const weekNumber = this.getWeekNumber(date)
        const weeklyKey = `${date.getFullYear()}-W${weekNumber}`
        const monthlyKey = `${date.getFullYear()}-${(date.getMonth() + 1)
          .toString()
          .padStart(2, '0')}`

        const initBucket = (key) => ({
          date: key,
          play: 0,
          download: 0,
          drm: 0,
          bandwidth: 0,
          cost: 0,
        })

        // --- diário ---
        if (!dailyMap[dailyKey]) {
          dailyMap[dailyKey] = initBucket(dailyKey)
        }
        dailyMap[dailyKey].play += item.b_general
        dailyMap[dailyKey].download += item.b_download
        dailyMap[dailyKey].drm += item.b_drm
        dailyMap[dailyKey].bandwidth += item.useds_bandwidth_total
        dailyMap[dailyKey].cost += item.cost

        // --- semanal ---
        if (!weeklyMap[weeklyKey]) {
          weeklyMap[weeklyKey] = initBucket(weeklyKey)
        }
        weeklyMap[weeklyKey].play += item.b_general
        weeklyMap[weeklyKey].download += item.b_download
        weeklyMap[weeklyKey].drm += item.b_drm
        weeklyMap[weeklyKey].bandwidth += item.useds_bandwidth_total
        weeklyMap[weeklyKey].cost += item.cost

        // --- mensal ---
        if (!monthlyMap[monthlyKey]) {
          monthlyMap[monthlyKey] = initBucket(monthlyKey)
        }
        monthlyMap[monthlyKey].play += item.b_general
        monthlyMap[monthlyKey].download += item.b_download
        monthlyMap[monthlyKey].drm += item.b_drm
        monthlyMap[monthlyKey].bandwidth += item.useds_bandwidth_total
        monthlyMap[monthlyKey].cost += item.cost
      })

      return {
        daily: (() => {
          const start = new Date(this.dateRange.start)
          const end = new Date(this.dateRange.end)
          const days = []
          let d = new Date(start)
          while (d <= end) {
            days.push(format(d, 'yyyy-MM-dd'))
            d.setDate(d.getDate() + 1)
          }
          return days.map((dateStr) => {
            return (
              dailyMap[dateStr] || {
                date: dateStr,
                play: 0,
                download: 0,
                drm: 0,
                bandwidth: 0,
                cost: 0,
              }
            )
          })
        })(),
        weekly: Object.values(weeklyMap).sort((a, b) =>
          a.date.localeCompare(b.date)
        ),
        monthly: Object.values(monthlyMap).sort((a, b) =>
          a.date.localeCompare(b.date)
        ),
      }
    },
    buildSeriesFromAggregatedData(data) {
      const props = ['play', 'download', 'drm', 'cost']

      return props.map((prop) => {
        const hex = this.metricColors[prop]
        const rgb = hex
          .replace('#', '')
          .match(/.{1,2}/g)
          .map((x) => parseInt(x, 16))

        const color =
          this.filterByMetric && this.filterByMetric !== prop
            ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.2)`
            : hex

        return {
          name: prop,
          data: data.map((entry) => entry[prop] || 0),
          type: prop === 'cost' ? 'line' : 'bar',
          color,
        }
      })
    },
    getDateOfISOWeek(week, year) {
      const simple = new Date(year, 0, 1 + (week - 1) * 7)
      const dow = simple.getDay()
      const ISOweekStart = simple
      if (dow <= 4) {
        ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1)
      } else {
        ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay())
      }
      return ISOweekStart
    },
    getWeekNumber(date) {
      const d = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
      )
      const dayNum = d.getUTCDay() || 7
      d.setUTCDate(d.getUTCDate() + 4 - dayNum)
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
      return Math.ceil(((d - yearStart) / 86400000 + 1) / 7)
    },
    getCategoriesFromData(data) {
      return data.map((entry) => entry.date)
    },
    sumPrecise(array) {
      if (!array) {
        return 0
      }

      return array.reduce((acc, val) => acc + parseFloat(val || 0), 0)
    },
    buildPlot(data) {
      this.chartSeries = this.buildSeriesFromAggregatedData(
        data[this.showPlotBy]
      )

      this.chartCategories = this.getCategoriesFromData(data[this.showPlotBy])
    },
    handleFilterByMetric(filter) {
      if (filter === this.filterByMetric) {
        this.filterByMetric = null
        return
      }

      this.filterByMetric = filter
    },
    handleGoToAnalyticsArticle() {
      const articleLink =
        this.analyticsLinks[this.$i18next.resolvedLanguage] ??
        this.analyticsLinks['en']
      window.open(articleLink, '_blank')
    },
    handleScroll(event) {
      const scrollTop = event.target.scrollTop
      this.isScrolled = scrollTop > 5
    },
    doItemsSearch() {
      let searchValue = this.searchByUrl
      if (searchValue && searchValue.length > 0) {
        let list = JSON.parse(JSON.stringify(this.aggregatedVideosToList))
        let fuseOptions = {
          keys: ['title'],
          includeScore: true,
          threshold: 0.4,
        }
        const fuse = new Fuse(list, fuseOptions)
        let searchResult = fuse.search(searchValue)
        const itemsResults = searchResult.map((result) => result.item)
        this.filteredRankingItems = itemsResults
      } else {
        this.filteredRankingItems = []
      }
    },
    clearSearch() {
      this.searchByUrl = ''
    },
    roundToNextBase10(number) {
      if (number <= 0) return 0
      const exp = Math.floor(Math.log10(number))
      const base = 10 ** exp
      return Math.ceil(number / base) * base
    },
    handleOpenBandwidthDetailsModal() {
      this.$root.$emit('openBandwidthDetailsModal')
    },
    clearTimeIntervals() {
      clearInterval(this.lastUpdatedTimeout)
      this.lastUpdatedTimeout = null
      this.lastUpdatedAt = 0
    },
    handleCurrentCycle(showOnlyCurrentCycle) {
      if (!showOnlyCurrentCycle) {
        this.UPDATE_FILTER_DATA_RANGE({
          start: subDays(new Date(), 31),
          end: subDays(new Date(), 1),
        })

        this.UPDATE_DISABLED_DATE_RANGE(false)

        return
      }

      const updateFilterDataRange = {
        start: new Date(this.userPlanInfo.limit?.start_period),
        end: new Date(this.userPlanInfo.limit?.end_period),
      }

      this.UPDATE_FILTER_DATA_RANGE(updateFilterDataRange)
      this.UPDATE_DISABLED_DATE_RANGE(true)
    },
    formatCurrency(value) {
      const valueToCast = value / 100 ?? 0
      return valueToCast.toLocaleString(this.locale, {
        style: 'currency',
        currency: this.userPlanInfo.currency || 'brl',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    generateAndDownloadCsv() {
      const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
      const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
      const tableData =
        this.searchByUrl.length > 0
          ? this.filteredRankingItems
          : this.aggregatedVideosToList
      exportToCsv(
        `analytics-bandwidth-${startDateFormatted}--${endDateFormatted}.csv`,
        this.currentlyVisibleColumns,
        tableData
      )
    },
    async generateAndDownloadPng() {
      this.isCapturingPng = true
      await this.$nextTick()
      await new Promise(resolve => setTimeout(resolve, 1000))
      const elementToCapture = this.$refs.chartContainerForCapture
      if (elementToCapture) {
        try {
          const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
          const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
          const filename = `bandwidth-${startDateFormatted}--${endDateFormatted}.png`
          await exportToPng(elementToCapture, filename)
        } catch (error) {
          this.UPDATE_ERROR_STATE(true)
        } finally {
          this.isCapturingPng = false
        }
      } else {
        console.error('Elemento para captura não foi encontrado.')
        this.isCapturingPng = false
      }
    },
    async generatePdf() {
      await this.$nextTick()
      const chartForPdf = this.$refs.fakeChart?.chart

      if (!chartForPdf) {
        console.error(
          'CRÍTICO: A instância do gráfico para PDF não foi encontrada.'
        )
        return
      }

      const headers = this.baseTableColumns.map((col) => col.text)
      const dataToExport =
        this.searchByUrl.length > 0
          ? this.filteredRankingItems
          : this.aggregatedVideosToList
      const sortedDataForPdf = [...dataToExport].sort(
        (a, b) => b.totalConsumption - a.totalConsumption
      )

      const body = sortedDataForPdf.map((item) => {
        return this.baseTableColumns.map((col) => {
          if (col.formatter) {
            return col.formatter(item[col.value], item).replace(/="?|"?/g, '')
          }
          return item[col.value] || '-'
        })
      })

      const chartHeaderData = {
        title: this.$t('message.pages.analyticsV3.screen_bandwidth.title'),
        description: this.$t(
          'message.pages.analyticsV3.screen_bandwidth.description'
        ),
        metrics: this.chartSeries.map((metric) => {
          const total = this.sumPrecise(metric.data)
          let formattedValue = ''

          if (metric.name === 'cost') {
            formattedValue = `R$ ${(total / 100).toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          } else {
            formattedValue = this.calcMegabytes(total, 'MB', 'GB', true)
          }

          return {
            label: `${this.$t(
              `message.pages.analyticsV3.screen_bandwidth.${metric.name}`
            )}:`,
            value: formattedValue,
            color: this.metricColors[metric.name],
          }
        }),
      }

      const columnStyles = {
        0: { cellWidth: 68, overflow: 'ellipsize' },
        1: { cellWidth: 22 },
        2: { cellWidth: 22 },
        3: { cellWidth: 22 },
        4: { cellWidth: 22 },
        5: { cellWidth: 30 },
      }

      const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
      const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
      const filename = `bandwidth-${startDateFormatted}--${endDateFormatted}.pdf`

      const pdfBlob = await generatePdfBlob({
        chartHeaderData: chartHeaderData,
        chartInstance: chartForPdf,
        tableHeaders: headers,
        tableBody: body,
        columnStyles: columnStyles,
      })
      this.$root.$emit('pdf-ready', { blob: pdfBlob, filename: filename })
    },
  },
  watch: {
    aggregatedDataToPlot(newVal) {
      this.buildPlot(newVal)
    },
    dateRange() {
      if (this.selectedVideos?.length > 0) {
        this.getSelectedInfo()
        return
      }

      this.fetchGlobalMetrics()
    },
    showPlotBy() {
      this.buildPlot(this.aggregatedDataToPlot)
    },
    filterByMetric() {
      this.buildPlot(this.aggregatedDataToPlot)
    },
    locale() {
      this.buildPlot(this.aggregatedDataToPlot)
    },
    showCurrentCycle(newVal) {
      this.handleCurrentCycle(newVal)
    },
    gbCost() {
      this.fetchGlobalMetrics()
    },
    selectedVideos() {
      if (this.selectedVideos?.length > 0) {
        this.getSelectedInfo()
        return
      }

      this.fetchGlobalMetrics()
    },
  },
  mounted() {
    this.$root.$on('trigger-csv-export', this.generateAndDownloadCsv)
    this.$root.$on('trigger-png-export', this.generateAndDownloadPng)
    this.$root.$on('trigger-pdf-export', this.generatePdf)

    if (this.selectedVideos?.length > 0) {
      this.getSelectedInfo()

      return
    }
    this.fetchGlobalMetrics()
  },
  beforeDestroy() {
    this.$root.$off('trigger-csv-export', this.generateAndDownloadCsv)
    this.$root.$off('trigger-png-export', this.generateAndDownloadPng)
    this.$root.$off('trigger-pdf-export', this.generatePdf)
  },
  created() {
    if (!window.db) {
      this.$root.$emit('initDexie')
    }
  },
  destroyed() {
    this.UPDATE_DISABLED_DATE_RANGE(false)
  },
}
</script>

<style lang="scss" scoped>
.container-plot {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  padding: 24px;
  gap: 24px;
  border-radius: 8px;
  background-color: varFigma(neutral-100-white);
}

.container-table {
  width: 100%;
  padding: 24px;
  border-radius: 8px;
  background-color: varFigma(neutral-100-white);
}

.chart-full {
  width: 100%;
  height: 300px;
  min-height: 300px;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
}

.metric-btn {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
  padding: 4px;
  :hover {
    background-color: varFigma(neutral-95-lightest);
    border-radius: 8px;
    cursor: pointer;
  }
}

.metric-btn--opacity {
  opacity: 0.2;
}

.truncate-cell {
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dual-chart {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-column-gap: 0;
  width: 100%;
  height: 300px;
}

.chart-main {
  margin: 0;
  padding: 0;
}

.axis-labels-container {
  display: flex;
  flex-direction: row;
  justify-content: start;
  height: 100%;
}

.axis-labels {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 242px;
}

.axis-labels-daily {
  height: 210px;
}

@media (max-width: 1440px) {
  .axis-labels {
    height: 240px;
  }

  .axis-labels-daily {
    height: 212px;
  }
}

.axis-label {
  font-size: 11px;
  color: varFigma(neutral-40-light);
  line-height: 1;
  user-select: none;
}

.cost-label {
  display: flex;
  height: 246px;
  font-size: 11px;
  color: varFigma(neutral-40-light);
  line-height: 1;
  user-select: none;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
}

.content-cards-row {
  gap: 24px;
  padding-top: 24px;
}

.cards-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  background-color: varFigma(neutral-99);
  padding: 24px;
  height: 152px;
  border-radius: 8px;
  gap: 4px;
}

.content-card-infos {
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  text-align: center;
}
</style>

<style lang="scss">
.my-badge-scope .v-badge__badge {
  height: 12px !important;
  width: 12px !important;
  min-width: 12px !important;
  max-height: 12px !important;
  padding: 0 !important;
  line-height: 12px !important;
  font-size: 0 !important;
}

.apexcharts-tooltip {
  background-color: varFigma(neutral-100-white) !important;
  color: varFigma(neutral-20-dark) !important;
  border: 1px solid varFigma(neutral-95-lightest) !important;
}

.apexcharts-tooltip-title {
  background-color: varFigma(neutral-95-lightest) !important;
  color: varFigma(neutral-20-dark) !important;
  font-weight: 500;
  padding: 6px 10px;
}

.pdf-export-chart {
  position: absolute;
  top: -9999px;
  left: -9999px;
  z-index: -1;
}

.capture-clone {
  position: absolute;
  top: -9999px;
  left: -9999px;
  pointer-events: none;
  height: 450px;
  width: 1250px; 
  background: white;
  box-sizing: border-box;
}
</style>
